import React, { useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { observer } from 'mobx-react'
import AiKitLogo from '../../assets/images/ai-kit-logo.svg'
import googleDriveLogo from '../../assets/images/google-drive-logo.svg'
import oneDriveLogo from '../../assets/images/one-drive-logo.svg'
import dropboxLogo from '../../assets/images/dropbox-logo.svg'
import { useKnowledgeBaseById } from '@/features/knowledge-bases/hooks'
import LinearProgress from '@/ui/LinearProgress'
import {
  checkStorageProviderOAuthSupport,
  LOCAL_STORAGE_PROVIDER,
} from '@/features/knowledge-bases/domain'
import { toast } from 'react-toastify'
import { ENABLE_DRIVE_INTEGRATIONS } from '@/config/feature-flags'
import rootStore from '../store'
import { StorageProviderConfig } from '@/features/knowledge-bases/api'
import TransitionLayout from '@/ui/TransitionLayout'
import DriveIntegrationExplanations from '@/components/DriveIntegrationExplanations'
import Heading from '@/ui/Heading'
import RadioOptionGroup from '@/ui/form/RadioOptionGroup'

type KnowledgeBaseFormValues = {
  name: string
  storage_provider_config: {
    authorized: boolean
    provider: string
  }
}

type KnowledgeBaseFormProps = {
  onSubmit: (data: KnowledgeBaseFormValues) => void
  defaultValues?: Partial<KnowledgeBaseFormValues>
}

const RADIO_OPTIONS = [
  {
    label: 'Gestión manual',
    value: LOCAL_STORAGE_PROVIDER,
    image: AiKitLogo,
  },
  {
    label: 'Microsoft OneDrive',
    value: 'onedrive',
    image: oneDriveLogo,
  },
  {
    label: 'Google Drive',
    value: 'google_drive',
    image: googleDriveLogo,
  },
  {
    label: 'Dropbox',
    value: 'dropbox',
    image: dropboxLogo,
  },
]

const KnowledgeBaseForm: React.FC<KnowledgeBaseFormProps> = ({
  defaultValues,
  onSubmit,
}) => {
  const currentProvider = defaultValues?.storage_provider_config?.provider
  const [radioOptionSelected, setRadioOptionSelected] = useState(
    currentProvider ?? LOCAL_STORAGE_PROVIDER,
  )

  const provider = radioOptionSelected

  const handleProviderChange = (provider: string) => {
    setRadioOptionSelected(provider)
  }

  const handleProviderSubmit = () => {
    onSubmit({
      name: defaultValues?.name ?? '',
      storage_provider_config: {
        authorized: false,
        provider,
      },
    })
  }

  return (
    <TransitionLayout>
      <div className="knowledge-page">
        <Heading
          variant="system"
          title="Método de sincronización"
          subtitle="En esta sección podrás escoger el método de sincronización al que se conectará la base de conocimiento. Selecciona y guarda el sistema de archivos de tu empresa."
          size="large"
        />
        <RadioOptionGroup
          name="withConfirmation"
          options={RADIO_OPTIONS}
          value={radioOptionSelected}
          isDisabled={radioOptionSelected === currentProvider}
          onChange={handleProviderChange}
          onConfirm={handleProviderSubmit}
          disclaimerText="Los servicios de terceros no son compatibles con la creación y gestión de espacios por departamento."
          confirmButtonText="Guardar"
        />

        <DriveIntegrationExplanations
          provider={
            provider as
              | 'google_drive'
              | 'onedrive'
              | 'dropbox'
              | 'azure_blob_storage'
          }
        />
      </div>
    </TransitionLayout>
  )
}

export const KnowledgeBaseFormPage = observer(() => {
  const navigate = useNavigate()
  const params = useParams()
  const knowledgeBaseId = params.id

  const { knowledgeBases } = rootStore
  const { isLoading, knowledgeBase } = useKnowledgeBaseById(knowledgeBaseId)

  const triggerOAuthFlowStart = async (
    modifiedStorageProviderConfig: StorageProviderConfig,
  ) => {
    const newProvider = modifiedStorageProviderConfig.provider
    const redirectURL = `${window.location.origin}/knowledge-bases/oauth-callback/${newProvider}`

    await knowledgeBases.startOAuthFlow(
      newProvider,
      knowledgeBaseId!!,
      redirectURL,
    )
  }

  const updateKnowledgeBase = async (
    knowledgeBaseId: string,
    updatedStorageProviderConfig: StorageProviderConfig,
  ) => {
    await knowledgeBases.update(knowledgeBaseId, {
      storage_provider_config: updatedStorageProviderConfig,
    })

    navigate(`/knowledge-bases/${knowledgeBaseId}`)

    toast.success('Base de conocimiento actualizada con éxito.')
  }

  // eslint-disable-next-line no-unused-vars
  const handleCreate = async (_data: KnowledgeBaseFormValues) => {
    // NOTE: Creation is not allowed yet
    // 1. Create KB
    // 2. `checkStorageProviderOAuthSupport()`
    // 2. `triggerOAuthFlowStart()`
  }

  const handleEdit = async (data: KnowledgeBaseFormValues) => {
    if (!knowledgeBaseId || !knowledgeBase) {
      // NOTE: This should never happen
      return
    }

    // TODO: Update other KB fields. Not implemented yet.
    const { storage_provider_config: updatedStorageProviderConfig } = data

    const providerChanged =
      knowledgeBase.storage_provider_config.provider !==
      updatedStorageProviderConfig.provider

    if (providerChanged) {
      const acceptsChange = confirm(
        '¿Estás seguro de que deseas cambiar de proveedor de almacenamiento? Se perderán los datos actuales.',
      )

      if (!acceptsChange) {
        return
      }

      // NOTE: Provider doesn't require OAuth
      if (
        !checkStorageProviderOAuthSupport(updatedStorageProviderConfig.provider)
      ) {
        // @ts-ignore - Provider enum works as string
        await updateKnowledgeBase(knowledgeBaseId, updatedStorageProviderConfig)
        return
      }

      // NOTE: Provider changed and requires OAuth
      await triggerOAuthFlowStart(updatedStorageProviderConfig as any)
    } else if (updatedStorageProviderConfig.authorized) {
      // NOTE: Provider stays the same and is already authorized. Nothing to do.
      return
    }
  }

  const onSubmit = async (data: KnowledgeBaseFormValues) => {
    if (!ENABLE_DRIVE_INTEGRATIONS) {
      return
    }

    const creationMode = !knowledgeBaseId

    if (creationMode) {
      // NOTE: Do nothing, creation is not allowed yet
      await handleCreate(data)
    } else {
      await handleEdit(data)
    }
  }

  if (!knowledgeBaseId) {
    return <Navigate to="/404" replace />
  }

  if (isLoading) {
    return (
      <div className="top-loading-bar">
        <LinearProgress />
      </div>
    )
  }

  if (!knowledgeBase) {
    return <div>Knowledge Base not found</div>
  }

  return (
    <KnowledgeBaseForm
      onSubmit={onSubmit}
      defaultValues={{
        name: knowledgeBase.name,
        storage_provider_config: {
          provider: knowledgeBase.storage_provider_config.provider as string,
          authorized: !!knowledgeBase.storage_provider_config.authorized,
        },
      }}
    />
  )
})
